import { useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { ConfirmDialog } from "@/base/ConfirmDialog";
import { RiderPendingBanner } from "@/components/bond-submission/SubmissionShell";
import {
  pendingRiderRequestedAt,
  withdrawBondRider,
} from "@/lib/bondServicing";
import { readChangeRequest } from "@/lib/bondStateModel";
import type { BondSubmission } from "@/lib/brokerBonds";
import { anchorServerError } from "@/lib/serverFieldErrors";

// BRK-S420 — the submission page's "Rider request pending" banner, live:
// Withdraw request asks first (the shared ConfirmDialog), then posts the
// withdraw route for the rider `changeRequest.id` names (PMS-S850). The 2xx is the order view and the page shows it, so the banner
// clears from the server's answer and nothing else; a refusal is said in the
// banner, which stays (#7).

export function RiderPendingNotice({
  order,
  onUpdated,
}: {
  order: BondSubmission;
  /** The order view a 2xx answered with: the page shows it. */
  onUpdated: (order: BondSubmission) => void;
}) {
  const { authedRequest } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const riderId = readChangeRequest(order.changeRequest)?.id;

  async function withdraw(id: string) {
    setBusy(true);
    setError(null);
    try {
      onUpdated(await withdrawBondRider(authedRequest, order.id, id));
    } catch (err) {
      setError(anchorServerError(err, []).summary);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <RiderPendingBanner
        requestedAt={pendingRiderRequestedAt(order)}
        onWithdraw={
          riderId === undefined ? undefined : () => setConfirming(true)
        }
        busy={busy}
        error={error}
      />
      <ConfirmDialog
        open={confirming}
        question="Withdraw this rider request?"
        confirmLabel="Withdraw request"
        cancelLabel="Keep request"
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          if (riderId !== undefined) void withdraw(riderId);
        }}
      >
        <p className="text-sm text-brk-text">
          The bond stays as issued, and you can request a different change
          afterwards.
        </p>
      </ConfirmDialog>
    </>
  );
}
